import { In } from 'typeorm'
import { Arg, Args, Ctx, FieldResolver, Query, Resolver, Root } from 'type-graphql'

import { Attachment } from '@things-factory/attachment-base'
import { User } from '@things-factory/auth-base'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'

import { DataKeySet } from './data-key-set.js'
import { DataKeySetList } from './data-key-set-type.js'

@Resolver(DataKeySet)
export class DataKeySetQuery {
  @Query(returns => DataKeySet!, {
    nullable: true,
    description:
      'Retrieves a single data key set by its unique identifier. This query fetches detailed information about a key set including its key items, configuration, and metadata. Data key sets define the indexing and categorization structure for data samples and summaries.'
  })
  async dataKeySet(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<DataKeySet> {
    const { domain } = context.state

    return await getRepository(DataKeySet).findOne({
      where: { domain: { id: In([domain.id, domain.parentId].filter(Boolean)) }, id }
    })
  }

  @Query(returns => DataKeySetList, {
    description:
      'Retrieves multiple data key sets with pagination and filtering support. This query allows searching key sets by name and description, and is used for listing and managing data categorization structures across all datasets.'
  })
  async dataKeySets(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<DataKeySetList> {
    const { domain } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      domain,
      params,
      repository: await getRepository(DataKeySet),
      searchables: ['name', 'description']
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @FieldResolver(type => String, {
    description:
      'Retrieves the thumbnail image path for this data key set. This field resolver provides access to visual representation of the key set, typically used for display purposes in user interfaces.'
  })
  async thumbnail(@Root() dataKeySet: DataKeySet): Promise<string | undefined> {
    const attachment: Attachment = await getRepository(Attachment).findOne({
      where: {
        domain: { id: dataKeySet.domainId },
        refType: DataKeySet.name,
        refBy: dataKeySet.id
      }
    })

    return attachment?.fullpath
  }

  @FieldResolver(type => Domain, {
    description:
      'Retrieves the domain that this data key set belongs to. This field resolver provides access to the organizational context and settings associated with the key set.'
  })
  async domain(@Root() dataKeySet: DataKeySet): Promise<Domain> {
    return dataKeySet.domainId && (await getRepository(Domain).findOneBy({ id: dataKeySet.domainId }))
  }

  @FieldResolver(type => User, {
    description:
      'Retrieves the user who last updated this data key set. This field resolver identifies the person responsible for the most recent modifications to the key set configuration.'
  })
  async updater(@Root() dataKeySet: DataKeySet): Promise<User> {
    return dataKeySet.updaterId && (await getRepository(User).findOneBy({ id: dataKeySet.updaterId }))
  }

  @FieldResolver(type => User, {
    description:
      'Retrieves the user who created this data key set. This field resolver identifies the person responsible for initially defining the key set structure and configuration.'
  })
  async creator(@Root() dataKeySet: DataKeySet): Promise<User> {
    return dataKeySet.creatorId && (await getRepository(User).findOneBy({ id: dataKeySet.creatorId }))
  }
}
